import {
    Connection,
    PublicKey,
    TransactionInstruction,
    SystemProgram,
    ComputeBudgetProgram,
    TransactionMessage,
    VersionedTransaction,
    AccountMeta,
    Keypair,
} from '@solana/web3.js';
import { getAssociatedTokenAddress, createAssociatedTokenAccountInstruction, getAssociatedTokenAddressSync, getAccount } from '@solana/spl-token';
import { WSOL } from '@raydium-io/raydium-sdk';
import { globalConfigPda, PUMP_AMM_PROGRAM_ID_PUBKEY, PumpAmmSdk } from '@pump-fun/pump-swap-sdk';
import BN from 'bn.js';
import { use0Slot } from './use0Slot';

export const usePumpswap = () => {
    const { get0SlotFeeInstruction } = use0Slot();
    type Direction = 'quoteToBase' | 'baseToQuote';

    const PUMP_AMM_PROGRAM_ID: PublicKey = new PublicKey('pAMMBay6oceH9fJKBRHGP5D4bD4sWpmSwMn52FMfXEA');
    const ASSOCIATED_TOKEN_PROGRAM_ID: PublicKey = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');
    const TOKEN_PROGRAM_ID: PublicKey = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
    const WSOL_TOKEN_ACCOUNT: PublicKey = new PublicKey('So11111111111111111111111111111111111111112');
    const global = new PublicKey('ADyA8hdefvWN2dbGGWFotbzWxrAvLW83WG6QCVXvJKqw');
    const eventAuthority = new PublicKey('GS4CU59F31iL7aR2Q8zVS8DRrcRnXX1yjQ66TqNVQnaR');
    const buyFeeRecipient = new PublicKey('62qc2CNXwrYqQScmEdiZFFAnJR262PxWEuNQtxfafNgV');
    const buyFeeRecipientAta = new PublicKey('94qWNrtmfn42h3ZjUZwWvK1MEo9uVmmrBPd2hpNjYDjb');
    const sellFeeRecipient = new PublicKey('7VtfL8fvgNfhz17qKRMjzQEXgbdpnHHHQRh54R9jP2RJ');
    const sellFeeRecipientAta = new PublicKey('7GFUN3bWzJMKMRZ34JLsvcqdssDbXnp589SiE33KVwcC');
    const BUY_DISCRIMINATOR: Uint8Array = new Uint8Array([102, 6, 61, 18, 1, 218, 235, 234]);
    const SELL_DISCRIMINATOR: Uint8Array = new Uint8Array([51, 230, 133, 164, 1, 127, 131, 173]);

    async function createBuyInstruction(
        poolId: PublicKey,
        user: PublicKey,
        mint: PublicKey,
        baseAmountOut: bigint, // Use bigint for u64
        maxQuoteAmountIn: bigint, // Use bigint for u64
    ): Promise<TransactionInstruction> {
        // Compute associated token account addresses
        const userBaseTokenAccount = await getAssociatedTokenAddress(mint, user);
        const userQuoteTokenAccount = await getAssociatedTokenAddress(WSOL_TOKEN_ACCOUNT, user);
        const poolBaseTokenAccount = await getAssociatedTokenAddress(mint, poolId, true);

        const poolQuoteTokenAccount = await getAssociatedTokenAddress(WSOL_TOKEN_ACCOUNT, poolId, true);

        // Define the accounts for the instruction
        const accounts = [
            { pubkey: poolId, isSigner: false, isWritable: false }, // pool_id (readonly)
            { pubkey: user, isSigner: true, isWritable: true }, // user (signer)
            { pubkey: global, isSigner: false, isWritable: false }, // global (readonly)
            { pubkey: mint, isSigner: false, isWritable: false }, // mint (readonly)
            { pubkey: WSOL_TOKEN_ACCOUNT, isSigner: false, isWritable: false }, // WSOL_TOKEN_ACCOUNT (readonly)
            { pubkey: userBaseTokenAccount, isSigner: false, isWritable: true }, // user_base_token_account
            { pubkey: userQuoteTokenAccount, isSigner: false, isWritable: true }, // user_quote_token_account
            { pubkey: poolBaseTokenAccount, isSigner: false, isWritable: true }, // pool_base_token_account
            { pubkey: poolQuoteTokenAccount, isSigner: false, isWritable: true }, // pool_quote_token_account
            { pubkey: buyFeeRecipient, isSigner: false, isWritable: false }, // fee_recipient (readonly)
            { pubkey: buyFeeRecipientAta, isSigner: false, isWritable: true }, // fee_recipient_ata
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // TOKEN_PROGRAM_ID (readonly)
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // TOKEN_PROGRAM_ID (readonly, duplicated as in Rust)
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false }, // System Program (readonly)
            { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // ASSOCIATED_TOKEN_PROGRAM_ID (readonly)
            { pubkey: eventAuthority, isSigner: false, isWritable: false }, // event_authority (readonly)
            { pubkey: PUMP_AMM_PROGRAM_ID, isSigner: false, isWritable: false }, // PUMP_AMM_PROGRAM_ID (readonly)
        ];

        // Pack the instruction data: discriminator (8 bytes) + base_amount_in (8 bytes) + min_quote_amount_out (8 bytes)
        const data = Buffer.alloc(8 + 8 + 8); // 24 bytes total
        data.set(BUY_DISCRIMINATOR, 0);
        data.writeBigUInt64LE(BigInt(baseAmountOut), 8); // Write base_amount_in as little-endian u64
        data.writeBigUInt64LE(BigInt(maxQuoteAmountIn), 16); // Write min_quote_amount_out as little-endian u64

        // Create the transaction instruction
        return new TransactionInstruction({
            keys: accounts,
            programId: PUMP_AMM_PROGRAM_ID,
            data: data,
        });
    }

    async function createSellInstruction(
        poolId: PublicKey,
        user: PublicKey,
        mint: PublicKey,
        baseAmountIn: bigint, // Use bigint for u64
        minQuoteAmountOut: bigint, // Use bigint for u64
    ): Promise<TransactionInstruction> {
        // Compute associated token account addresses
        const userBaseTokenAccount = await getAssociatedTokenAddress(mint, user);
        const userQuoteTokenAccount = await getAssociatedTokenAddress(WSOL_TOKEN_ACCOUNT, user);
        const poolBaseTokenAccount = await getAssociatedTokenAddress(mint, poolId, true);
        const poolQuoteTokenAccount = await getAssociatedTokenAddress(WSOL_TOKEN_ACCOUNT, poolId, true);

        // Define the accounts for the instruction
        const accounts = [
            { pubkey: poolId, isSigner: false, isWritable: false }, // pool_id (readonly)
            { pubkey: user, isSigner: true, isWritable: true }, // user (signer)
            { pubkey: global, isSigner: false, isWritable: false }, // global (readonly)
            { pubkey: mint, isSigner: false, isWritable: false }, // mint (readonly)
            { pubkey: WSOL_TOKEN_ACCOUNT, isSigner: false, isWritable: false }, // WSOL_TOKEN_ACCOUNT (readonly)
            { pubkey: userBaseTokenAccount, isSigner: false, isWritable: true }, // user_base_token_account
            { pubkey: userQuoteTokenAccount, isSigner: false, isWritable: true }, // user_quote_token_account
            { pubkey: poolBaseTokenAccount, isSigner: false, isWritable: true }, // pool_base_token_account
            { pubkey: poolQuoteTokenAccount, isSigner: false, isWritable: true }, // pool_quote_token_account
            { pubkey: sellFeeRecipient, isSigner: false, isWritable: false }, // fee_recipient (readonly)
            { pubkey: sellFeeRecipientAta, isSigner: false, isWritable: true }, // fee_recipient_ata
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // TOKEN_PROGRAM_ID (readonly)
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // TOKEN_PROGRAM_ID (readonly, duplicated as in Rust)
            { pubkey: SystemProgram.programId, isSigner: false, isWritable: false }, // System Program (readonly)
            { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }, // ASSOCIATED_TOKEN_PROGRAM_ID (readonly)
            { pubkey: eventAuthority, isSigner: false, isWritable: false }, // event_authority (readonly)
            { pubkey: PUMP_AMM_PROGRAM_ID, isSigner: false, isWritable: false }, // PUMP_AMM_PROGRAM_ID (readonly)
        ];

        // Pack the instruction data: discriminator (8 bytes) + base_amount_in (8 bytes) + min_quote_amount_out (8 bytes)
        const data = Buffer.alloc(8 + 8 + 8); // 24 bytes total
        data.set(SELL_DISCRIMINATOR, 0);
        data.writeBigUInt64LE(BigInt(baseAmountIn), 8); // Write base_amount_in as little-endian u64
        data.writeBigUInt64LE(BigInt(minQuoteAmountOut), 16); // Write min_quote_amount_out as little-endian u64

        // Create the transaction instruction
        return new TransactionInstruction({
            keys: accounts,
            programId: PUMP_AMM_PROGRAM_ID,
            data: data,
        });
    }

    function createCloseUserVolumeAccumulatorIx(user: PublicKey): TransactionInstruction {
        const CLOSE_USER_VOLUME_ACCUMULATOR_DISCRIMINATOR = Buffer.from([249, 69, 164, 218, 150, 103, 84, 138]);

        const [userVolumeAccumulator] = PublicKey.findProgramAddressSync([Buffer.from('user_volume_accumulator'), user.toBuffer()], PUMP_AMM_PROGRAM_ID);
        const eventAuthority = new PublicKey('GS4CU59F31iL7aR2Q8zVS8DRrcRnXX1yjQ66TqNVQnaR');

        const accounts = [
            { pubkey: user, isSigner: true, isWritable: true },
            { pubkey: userVolumeAccumulator, isSigner: false, isWritable: true },
            { pubkey: eventAuthority, isSigner: false, isWritable: false },
            { pubkey: PUMP_AMM_PROGRAM_ID, isSigner: false, isWritable: false },
        ];

        return new TransactionInstruction({
            keys: accounts,
            programId: PUMP_AMM_PROGRAM_ID,
            data: CLOSE_USER_VOLUME_ACCUMULATOR_DISCRIMINATOR,
        });
    }

    async function burnAllUserVolumeAccumulators(
        connection: Connection,
        users: Keypair[],
        priorityFee: number,
        logMessage: (message: string, type?: 'success' | 'error') => void,
    ) {
        const instructions: TransactionInstruction[] = [];
        const singers: Keypair[] = [];
        for (const user of users) {
            const userVolumeAccumulator = PublicKey.findProgramAddressSync(
                [Buffer.from('user_volume_accumulator'), user.publicKey.toBuffer()],
                PUMP_AMM_PROGRAM_ID,
            )[0];
            const userVolumeAccumulatorInfo = await connection.getAccountInfo(userVolumeAccumulator, 'processed');
            if (!userVolumeAccumulatorInfo || userVolumeAccumulatorInfo.data.length === 0) {
                continue;
            }
            console.log(`userVolumeAccumulator: ${userVolumeAccumulator.toBase58()}`);
            const closeIx = createCloseUserVolumeAccumulatorIx(user.publicKey);
            instructions.push(closeIx);
            singers.push(user);
        }
        logMessage(`Found ${instructions.length} user volume accumulator account[s] to burn.`);

        const instructionsPerTransaction = 10;
        for (let i = 0; i < instructions.length; i += instructionsPerTransaction) {
            const batchIxs = instructions.slice(i, i + instructionsPerTransaction);
            const batchSigners = singers.slice(i, i + instructionsPerTransaction);
            const tx = new VersionedTransaction(
                new TransactionMessage({
                    payerKey: batchSigners[0].publicKey,
                    recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                    instructions: [
                        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }),
                        ComputeBudgetProgram.setComputeUnitLimit({ units: 100000 }),
                        ...batchIxs,
                    ],
                }).compileToV0Message(),
            );
            tx.sign(batchSigners);
            const hash = await connection.sendTransaction(tx, { skipPreflight: true });

            logMessage(`Burn volume tx sent: ${hash}`);
        }

        logMessage(`Burned all user volume accumulators for ${singers.length} users.`, 'success');
    }

    async function buildCreatePoolTx(
        connection: Connection,
        quoteMint: string,
        quoteAmount: number,
        quoteDecimals: number,
        baseAmount: number,
        userPublicKey: string,
        index: number = 0,
    ) {
        const sdk = new PumpAmmSdk(connection);
        const userPubkey = new PublicKey(userPublicKey);
        const baseMintPubkey = new PublicKey(WSOL.mint);
        const quoteMintPubkey = new PublicKey(quoteMint);
        const baseDecimals = WSOL.decimals;
        const baseAmountLamports = Math.floor(baseAmount * Math.pow(10, baseDecimals));
        const quoteAmountLamports = Math.floor(quoteAmount * Math.pow(10, quoteDecimals));
        const baseAmountBN = new BN(baseAmountLamports);
        const quoteAmountBN = new BN(quoteAmountLamports);

        let ixs: TransactionInstruction[] = [];

        const [poolAddress] = PublicKey.findProgramAddressSync(
            [Buffer.from('pump_pool'), Buffer.from([index]), baseMintPubkey.toBuffer(), quoteMintPubkey.toBuffer()],
            PUMP_AMM_PROGRAM_ID,
        );
        const baseTokenAccount = await getAssociatedTokenAddress(baseMintPubkey, poolAddress, true);

        const quoteTokenAccount = await getAssociatedTokenAddress(quoteMintPubkey, poolAddress, true);

        console.log('Attempting to create token accounts for pool');
        const createBaseTokenAccountIx = createAssociatedTokenAccountInstruction(
            userPubkey, // payer
            baseTokenAccount, // associatedToken
            poolAddress, // owner
            baseMintPubkey, // mint
        );

        const createQuoteTokenAccountIx = createAssociatedTokenAccountInstruction(
            userPubkey, // payer
            quoteTokenAccount, // associatedToken
            poolAddress, // owner
            quoteMintPubkey, // mint
        );

        ixs.push(createBaseTokenAccountIx);
        ixs.push(createQuoteTokenAccountIx);

        const createPoolInstructions = await sdk.createPoolInstructions(index, userPubkey, baseMintPubkey, quoteMintPubkey, baseAmountBN, quoteAmountBN);

        console.log(`SDK generated ${createPoolInstructions.length} instructions for pool creation`);

        ixs.push(...createPoolInstructions);

        const message = new TransactionMessage({
            payerKey: userPubkey,
            recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
            instructions: ixs,
        }).compileToV0Message();

        const versionedTransaction = new VersionedTransaction(message);

        return { deployTx: versionedTransaction, poolAddress: poolAddress, deployIxs: ixs };
    }

    async function buildRemoveLiquidityTx(
        connection: Connection,
        pool: string,
        lpTokenAmount: number,
        userPublicKey: string,
        tipLamports: number,
        priorityLimit?: number,
        priorityFee?: number,
        extraIxs: TransactionInstruction[] = [],
    ) {
        const sdk = new PumpAmmSdk(connection);

        const userPubkey = new PublicKey(userPublicKey);
        const poolAddress = new PublicKey(pool);

        console.log(`Building remove liquidity transaction for pool: ${poolAddress.toBase58()}`);

        const poolData = await connection.getAccountInfo(poolAddress, 'processed');
        if (!poolData) {
            throw new Error('Failed to fetch pool data');
        }

        const lpTokenAmountBN = new BN(lpTokenAmount);

        const ixs: TransactionInstruction[] = [];

        console.log(`Getting withdraw instructions with LP token amount: ${lpTokenAmountBN.toString()}`);
        const withdrawInstructions = await sdk.withdrawInstructions(poolAddress, lpTokenAmountBN, 100, userPubkey);

        console.log(`Generated ${withdrawInstructions.length} withdraw instructions`);

        ixs.push(...withdrawInstructions);
        if (priorityLimit) ixs.push(ComputeBudgetProgram.setComputeUnitLimit({ units: priorityLimit }));
        if (priorityFee) ixs.push(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }));

        ixs.push(...extraIxs);
        ixs.push(get0SlotFeeInstruction(userPubkey, tipLamports));
        const message = new TransactionMessage({
            payerKey: userPubkey,
            recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
            instructions: ixs,
        }).compileToV0Message();
        const versionedTransaction = new VersionedTransaction(message);

        return versionedTransaction;
    }

    async function buildSwapTx(
        connection: Connection,
        pool: string,
        inputAmount: number,
        direction: Direction,
        userPublicKey: string,
        priorityFee: number = 0,
    ) {
        const sdk = new PumpAmmSdk(connection);
        const userPubkey = new PublicKey(userPublicKey);
        const poolAddress = new PublicKey(pool);

        const inputAmountBN = new BN(inputAmount);
        const ixs: TransactionInstruction[] = [];

        let swapInstructions: TransactionInstruction[] = await sdk.swapQuoteInstructions(poolAddress, inputAmountBN, 0, direction, userPubkey);

        ixs.push(...swapInstructions);
        ixs.push(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }));
        const message = new TransactionMessage({
            payerKey: userPubkey,
            recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
            instructions: ixs,
        }).compileToV0Message();
        const versionedTransaction = new VersionedTransaction(message);

        return versionedTransaction;
    }

    async function getPoolId(owner: PublicKey, baseMint: PublicKey, quoteMint: PublicKey, index: number = 0) {
        const poolPda = (index: number, owner: PublicKey, baseMint: PublicKey, quoteMint: PublicKey) =>
            PublicKey.findProgramAddressSync(
                [Buffer.from('pool'), new BN(index).toArrayLike(Buffer, 'le', 2), owner.toBuffer(), baseMint.toBuffer(), quoteMint.toBuffer()],
                PUMP_AMM_PROGRAM_ID,
            )[0];

        return poolPda(index, owner, baseMint, quoteMint);
    }

    function getBuyIx(
        pool: PublicKey,
        baseMint: PublicKey,
        quoteMint: PublicKey,
        user: PublicKey,
        baseAmountOut: bigint,
        maxQuoteAmountIn: bigint,
        coinCreator = new PublicKey('11111111111111111111111111111111'),
        protocolFeeRecipient = new PublicKey('7VtfL8fvgNfhz17qKRMjzQEXgbdpnHHHQRh54R9jP2RJ'),
        programId = PUMP_AMM_PROGRAM_ID_PUBKEY,
        baseTokenProgramId = TOKEN_PROGRAM_ID,
        quoteTokenProgramId = TOKEN_PROGRAM_ID,
    ): TransactionInstruction {
        const coinCreatorVaultAuthorityPubkey = PublicKey.findProgramAddressSync(
            [Buffer.from([99, 114, 101, 97, 116, 111, 114, 95, 118, 97, 117, 108, 116]), coinCreator.toBuffer()],
            programId,
        )[0];

        const keys: Array<AccountMeta> = [
            {
                pubkey: pool,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: user,
                isSigner: true,
                isWritable: true,
            },
            {
                pubkey: globalConfigPda(programId)[0],
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: baseMint,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: quoteMint,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: getAssociatedTokenAddressSync(baseMint, user, false, baseTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(quoteMint, user, false, quoteTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(baseMint, pool, true, baseTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(quoteMint, pool, true, quoteTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: protocolFeeRecipient,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [protocolFeeRecipient.toBuffer(), quoteTokenProgramId.toBuffer(), quoteMint.toBuffer()],
                    new PublicKey([
                        140, 151, 37, 143, 78, 36, 137, 241, 187, 61, 16, 41, 20, 142, 13, 131, 11, 90, 19, 153, 218, 255, 16, 132, 4, 142, 123, 216, 219, 233,
                        248, 89,
                    ]),
                )[0],
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: baseTokenProgramId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: quoteTokenProgramId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: SystemProgram.programId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [Buffer.from([95, 95, 101, 118, 101, 110, 116, 95, 97, 117, 116, 104, 111, 114, 105, 116, 121])],
                    programId,
                )[0],
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: programId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [coinCreatorVaultAuthorityPubkey.toBuffer(), quoteTokenProgramId.toBuffer(), quoteMint.toBuffer()],
                    new PublicKey([
                        140, 151, 37, 143, 78, 36, 137, 241, 187, 61, 16, 41, 20, 142, 13, 131, 11, 90, 19, 153, 218, 255, 16, 132, 4, 142, 123, 216, 219, 233,
                        248, 89,
                    ]),
                )[0],
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: coinCreatorVaultAuthorityPubkey,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync([Buffer.from('global_volume_accumulator')], programId)[0],
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: PublicKey.findProgramAddressSync([Buffer.from('user_volume_accumulator'), user.toBuffer()], programId)[0],
                isSigner: false,
                isWritable: true,
            },
        ];

        const data = Buffer.alloc(8 + 8 + 8);
        data.set([102, 6, 61, 18, 1, 218, 235, 234], 0);
        data.writeBigUInt64LE(baseAmountOut, 8);
        data.writeBigUInt64LE(maxQuoteAmountIn, 16);

        return new TransactionInstruction({
            keys,
            data,
            programId,
        });
    }

    function getSellIx(
        pool: PublicKey,
        baseMint: PublicKey,
        quoteMint: PublicKey,
        user: PublicKey,
        baseAmountIn: bigint,
        minQuoteAmountOut: bigint,
        coinCreator = new PublicKey('11111111111111111111111111111111'),
        protocolFeeRecipient = new PublicKey('7VtfL8fvgNfhz17qKRMjzQEXgbdpnHHHQRh54R9jP2RJ'),
        programId = PUMP_AMM_PROGRAM_ID_PUBKEY,
        baseTokenProgramId = TOKEN_PROGRAM_ID,
        quoteTokenProgramId = TOKEN_PROGRAM_ID,
    ): TransactionInstruction {
        const coinCreatorVaultAuthorityPubkey = PublicKey.findProgramAddressSync(
            [Buffer.from([99, 114, 101, 97, 116, 111, 114, 95, 118, 97, 117, 108, 116]), coinCreator.toBuffer()],
            programId,
        )[0];

        const keys: Array<AccountMeta> = [
            {
                pubkey: pool,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: user,
                isSigner: true,
                isWritable: true,
            },
            {
                pubkey: globalConfigPda(programId)[0],
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: baseMint,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: quoteMint,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: getAssociatedTokenAddressSync(baseMint, user, false, baseTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(quoteMint, user, false, quoteTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(baseMint, pool, true, baseTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: getAssociatedTokenAddressSync(quoteMint, pool, true, quoteTokenProgramId),
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: protocolFeeRecipient,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [protocolFeeRecipient.toBuffer(), quoteTokenProgramId.toBuffer(), quoteMint.toBuffer()],
                    new PublicKey([
                        140, 151, 37, 143, 78, 36, 137, 241, 187, 61, 16, 41, 20, 142, 13, 131, 11, 90, 19, 153, 218, 255, 16, 132, 4, 142, 123, 216, 219, 233,
                        248, 89,
                    ]),
                )[0],
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: baseTokenProgramId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: quoteTokenProgramId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: SystemProgram.programId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [Buffer.from([95, 95, 101, 118, 101, 110, 116, 95, 97, 117, 116, 104, 111, 114, 105, 116, 121])],
                    programId,
                )[0],
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: programId,
                isSigner: false,
                isWritable: false,
            },
            {
                pubkey: PublicKey.findProgramAddressSync(
                    [coinCreatorVaultAuthorityPubkey.toBuffer(), quoteTokenProgramId.toBuffer(), quoteMint.toBuffer()],
                    new PublicKey([
                        140, 151, 37, 143, 78, 36, 137, 241, 187, 61, 16, 41, 20, 142, 13, 131, 11, 90, 19, 153, 218, 255, 16, 132, 4, 142, 123, 216, 219, 233,
                        248, 89,
                    ]),
                )[0],
                isSigner: false,
                isWritable: true,
            },
            {
                pubkey: coinCreatorVaultAuthorityPubkey,
                isSigner: false,
                isWritable: false,
            },
        ];

        const data = Buffer.alloc(8 + 8 + 8);
        data.set([51, 230, 133, 164, 1, 127, 131, 173], 0);
        data.writeBigUInt64LE(baseAmountIn, 8);
        data.writeBigUInt64LE(minQuoteAmountOut, 16);

        return new TransactionInstruction({
            keys,
            data,
            programId,
        });
    }

    async function calculateBaseFromQuote(connection: Connection, pool: PublicKey, quoteAmount: BN, slippage: number) {
        const sdk = new PumpAmmSdk(connection);
        const baseAmountOut = await sdk.swapAutocompleteBaseFromQuote(pool, quoteAmount, slippage, 'quoteToBase');

        return baseAmountOut;
    }

    async function calculateQuoteFromBase(connection: Connection, pool: PublicKey, baseAmount: BN, slippage: number) {
        const sdk = new PumpAmmSdk(connection);
        const quoteAmountOut = await sdk.swapAutocompleteQuoteFromBase(pool, baseAmount, slippage, 'baseToQuote');

        return quoteAmountOut;
    }

    async function getLpMint(connection: Connection, pool: PublicKey) {
        const sdk = new PumpAmmSdk(connection);
        const lpMint = await sdk.fetchPool(pool).then((poolData) => poolData.lpMint);
        return lpMint;
    }

    async function getPoolBaseTokenBalance(connection: Connection, pool: PublicKey) {
        const sdk = new PumpAmmSdk(connection);
        const poolData = await sdk.fetchPool(pool);
        const baseTokenAccount = poolData.poolBaseTokenAccount;
        return await getAccount(connection, baseTokenAccount, 'processed').then((account) => account.amount);
    }

    return {
        createBuyInstruction,
        createSellInstruction,
        buildCreatePoolTx,
        buildRemoveLiquidityTx,
        buildSwapTx,
        getPoolId,
        getBuyIx,
        getSellIx,
        calculateBaseFromQuote,
        calculateQuoteFromBase,
        getLpMint,
        getPoolBaseTokenBalance,
        createCloseUserVolumeAccumulatorIx,
        burnAllUserVolumeAccumulators,
    };
};
